import React, { useState, useEffect, useRef, useCallback } from 'react';
import ToolbarContent from './ToolbarContent';
import useResizeObserver from '@hooks/useResizeObserver';
import useStickyOffset from '@hooks/useStickyOffset';
import * as styles from './Toolbar.module.scss';
import classNames from 'classnames';

const contentSentinelOffset = parseInt(styles.contentSentinelOffset);
const spacerTopHeight = parseInt(styles.topMargin);

export default React.memo(function Toolbar({
  offset = 0,
  isDisabled,
  isStatic,
  ...rest
}) {
  const wrapper = useRef();
  const { height } = useResizeObserver(wrapper);
  const topSentinel = useRef();
  const contentSentinel = useRef();
  const bottomSentinel = useRef();
  const headerHeight = useStickyOffset();
  const isMounted = useRef(true);

  const [stickyState, _setStickyState] = useState({
    started: false,
    showContent: false,
    finished: false
  });

  const setStickyState = useCallback(state => {
    _setStickyState(prevState => {
      const newState = { ...prevState, ...state };

      document.body.classList.toggle(
        'sticky-toolbar',
        newState.started && !newState.finished
      );

      return newState;
    });
  }, []);

  useEffect(() => {
    document.body.classList.toggle('disable-sticky-toolbar', isStatic);

    return () => {
      isMounted.current = false;
      document.body.classList.remove('sticky-toolbar');
      document.body.classList.remove('disable-sticky-toolbar');
      document.body.classList.remove('calculating-sticky-toolbar');
    };
  }, [isStatic]);

  useEffect(() => {
    const topEl = topSentinel.current;
    const contentEl = contentSentinel.current;
    const bottomEl = bottomSentinel.current;

    let hasInitialCheck = false;
    let topObserver = null;
    let contentObserver = null;
    let bottomObserver = null;

    const initObservers = () => {
      topObserver = new IntersectionObserver(
        ([e]) => {
          const rect = e.boundingClientRect;
          const rootRect = e.rootBounds;
          const ratio = e.intersectionRatio;

          if (!rect || !rootRect) {
            return;
          }

          setStickyState({ started: ratio < 1 && rect.top <= rootRect.height });
        },
        { threshold: [1] }
      );

      contentObserver = new IntersectionObserver(
        ([e]) => {
          const rect = e.boundingClientRect;
          const rootRect = e.rootBounds;
          const ratio = e.intersectionRatio;

          if (!rect || !rootRect) {
            return;
          }

          setStickyState({
            showContent: ratio < 1 && rect.top < rootRect.height
          });
        },
        { threshold: [1] }
      );

      // Remove sticky class when user scrolls
      // past the end of the rate table
      bottomObserver = new IntersectionObserver(
        ([e]) => {
          const rect = e.boundingClientRect;
          const rootRect = e.rootBounds;
          const ratio = e.intersectionRatio;

          if (!rect || !rootRect) {
            return;
          }

          // User scrolled back up from below the rate table
          if (rect.bottom > rootRect.top && ratio === 1) {
            setStickyState({ finished: false });
          }

          // User scrolled past rate table
          if (rect.top < rootRect.top && rect.bottom <= rootRect.bottom) {
            setStickyState({ finished: true });
          }
        },
        { threshold: [1] }
      );

      if (topEl) {
        topObserver.observe(topEl);
      }

      if (contentEl) {
        contentObserver.observe(contentEl);
      }

      if (bottomEl) {
        bottomObserver.observe(bottomEl);
      }
    };

    // Perform initial sticky check manually since the
    // observers are unreliable when the page first loads
    document.body.classList.add('calculating-sticky-toolbar');

    let timer = null;

    const initSticky = (shouldRecalculate, recheckIfWrapperNotFound = true) => {
      clearTimeout(timer);

      if (!isMounted.current) {
        return;
      }

      const ratesWrapper = document.querySelector('#calculator-rates');
      const toolbar = wrapper.current;

      if ((!ratesWrapper || !toolbar) && recheckIfWrapperNotFound) {
        if (recheckIfWrapperNotFound) {
          timer = setTimeout(() => initSticky(true, false), 150);
        }

        return;
      }

      const wRect = ratesWrapper.getBoundingClientRect();
      const tRect = toolbar.getBoundingClientRect();
      const scroll = window.scrollY;

      const isAbove =
        wRect.top + spacerTopHeight + scroll >= tRect.top + scroll;
      const canShowContent =
        wRect.top + scroll + contentSentinelOffset <= tRect.top + scroll;
      const isBelow = wRect.bottom + scroll <= tRect.bottom + scroll;

      document.body.classList.toggle('sticky-toolbar', !isAbove && !isBelow);

      setStickyState({
        started: !isAbove,
        showContent: !isAbove && canShowContent,
        finished: isBelow
      });

      if (shouldRecalculate) {
        timer = setTimeout(() => initSticky(false, false), 150);
      }

      if (!topObserver && !contentObserver && !bottomObserver) {
        initObservers();
      }

      if (!hasInitialCheck) {
        hasInitialCheck = true;

        setTimeout(() => {
          document.body.classList.remove('calculating-sticky-toolbar');
        }, 50);
      }
    };

    const domReady = document.readyState === 'complete';

    initSticky(domReady);

    setTimeout(() => initSticky(), 100);

    if (!domReady) {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => initSticky(true), 100);
      });
    }

    let resizeTimer = null;

    const resizeHandler = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(initSticky, 100);
    };

    window.addEventListener('resize', resizeHandler);

    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimer);

      if (topEl && topObserver) {
        topObserver.unobserve(topEl);
      }

      if (contentEl && contentObserver) {
        topObserver.unobserve(contentEl);
      }

      if (bottomEl && bottomObserver) {
        bottomObserver.unobserve(bottomEl);
      }

      window.removeEventListener('resize', resizeHandler);
    };
  }, [setStickyState]);

  return (
    <>
      <div className={styles.spacerTop} />
      <div
        ref={wrapper}
        className={classNames(styles.wrapper, {
          [styles.finishedSticking]: stickyState.finished,
          [styles.showContent]: stickyState.showContent,
          [styles.static]: isStatic
        })}
        style={{ top: headerHeight - offset }}
      >
        <ToolbarContent
          isSticky={stickyState.started}
          canShowContent={stickyState.showContent}
          isDisabled={isDisabled}
          {...rest}
        />
        <div
          ref={topSentinel}
          className={styles.topSentinel}
        />
      </div>
      <div
        ref={contentSentinel}
        className={styles.contentSentinel}
      />
      <div
        ref={bottomSentinel}
        className={styles.bottomSentinel}
        style={{ height }}
      />
      <div className={styles.spacerBottom} />
    </>
  );
});
